import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import Login from "./components/Login";
import PlaylistResult from "./components/PlaylistResult";
import History from "./components/History";
import SessionOptions from "./components/SessionOptions";
import MoodOfDay from "./components/MoodOfDay";
import Favorites from "./components/Favorites";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import MoodPicker from "./components/MoodPicker";
import DurationPicker from "./components/DurationPicker";
import { GenerateBar, SyncBanner } from "./components/GenerateBar";
import useGlimpseSession from "./hooks/useGlimpseSession";
import useSessionForm from "./hooks/useSessionForm";
import api from "./lib/api";
import { MOOD_META } from "./lib/moods";
import { greeting } from "./lib/daily";

const scrollTo = (id, delay = 100) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), delay);

const toPayload = (cfg) => ({
  ...(cfg.custom_mood ? { custom_mood: cfg.custom_mood } : { mood: cfg.mood }),
  duration_minutes: cfg.duration_minutes,
  exclude_recent: !!cfg.exclude_recent,
  ...(cfg.mood2 ? { mood2: cfg.mood2, blend: cfg.blend } : {}),
});

const favoriteName = (cfg) => {
  const base = cfg.custom_mood || MOOD_META[cfg.mood].name;
  const mix = cfg.mood2 ? ` × ${MOOD_META[cfg.mood2].name} ${100 - cfg.blend}/${cfg.blend}` : "";
  return `${base}${mix} · ${cfg.duration_minutes} min`.slice(0, 80);
};

const TOAST_STYLE = { style: { background: "#FF2E93", color: "#000", border: "none", fontWeight: 600 } };

export default function App() {
  const s = useGlimpseSession();
  const f = useSessionForm();
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const runGenerate = async (cfg) => {
    if (s.library.count === 0) return toast.error("Synchronise ta bibliothèque d'abord");
    setGenerating(true);
    setResult(null);
    try {
      const r = await api.post("/generate", toPayload(cfg));
      setResult(r.data);
      await s.refreshHistory();
      scrollTo("result-anchor", 150);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => (f.error ? toast.error(f.error) : runGenerate(f.cfg));
  const launchCfg = (cfg) => { f.applyCfg(cfg); runGenerate(cfg); };
  const regenerate = (g) => {
    if (g.mood === "custom" && !g.custom_mood) return handleGenerate();
    launchCfg({
      mood: g.mood === "custom" ? null : g.mood,
      custom_mood: g.custom_mood || null,
      mood2: g.mood2 || null,
      blend: g.blend || 50,
      duration_minutes: g.duration_minutes,
      exclude_recent: !!g.exclude_recent,
    });
  };
  const openGeneration = (g) => { setResult(g); scrollTo("result-anchor"); };

  const saveFavorite = async () => {
    if (f.error) return toast.error(f.error);
    try {
      await api.post("/favorites", { ...f.cfg, name: favoriteName(f.cfg) });
      toast.success("Ajouté à tes favoris");
      s.refreshFavorites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'ajouter le favori");
    }
  };

  if (s.bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF2E93]" />
      </div>
    );
  }

  if (!s.session) {
    return (
      <>
        <Login onLogin={s.login} error={s.authError} />
        <Toaster theme="dark" />
      </>
    );
  }

  const noLibrary = s.library.count === 0;
  const label = f.selectedMood
    ? `${f.moodName}${f.mood2 ? ` × ${MOOD_META[f.mood2].name}` : ""} · ${f.duration} min`
    : "Choisis un mood pour commencer";

  return (
    <div className="h-screen bg-black p-2 flex gap-2 overflow-hidden">
      <Sidebar library={s.library} syncing={s.syncing} onSync={s.sync} history={s.history} onOpen={openGeneration} onNav={(id) => scrollTo(id, 0)} />

      <main className="flex-1 bg-[#121212] rounded-lg overflow-y-auto relative">
        <div id="top-anchor" className="sp-gradient-header absolute inset-x-0 top-0 h-80 pointer-events-none" />
        <TopBar session={s.session} syncing={s.syncing} onSync={s.sync} onLogout={() => { s.logout(); setResult(null); }} />

        <div className="relative z-10 px-6 pb-16">
          <h1 data-testid="greeting" className="font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2">
            {greeting()}{s.session.display_name ? `, ${s.session.display_name.split(" ")[0]}` : ""}
          </h1>
          <p className="mt-2 text-[#B3B3B3] text-sm md:text-base">Lance le mood du jour en un tap, ou compose ta session sur mesure.</p>

          <MoodOfDay onLaunch={launchCfg} disabled={generating || noLibrary} />
          <Favorites items={s.favorites} onLaunch={launchCfg} onDelete={s.deleteFavorite} disabled={generating || noLibrary} />
          {noLibrary && <SyncBanner syncing={s.syncing} onSync={s.sync} />}

          <MoodPicker moods={s.moods} selected={f.selectedMood} onSelect={f.setSelectedMood} customMood={f.customMood} setCustomMood={f.setCustomMood} onSubmit={handleGenerate} />
          <DurationPicker duration={f.duration} setDuration={f.setDuration} />
          <SessionOptions
            primary={f.selectedMood} primaryName={f.moodName}
            blendOn={f.blendOn} setBlendOn={f.setBlendOn} mood2={f.mood2} setMood2={f.setMood2}
            blend={f.blend} setBlend={f.setBlend} excludeRecent={f.excludeRecent} setExcludeRecent={f.setExcludeRecent}
          />
          <GenerateBar
            label={label} generating={generating} disabled={generating || !f.selectedMood || noLibrary}
            canSaveFavorite={!!f.selectedMood} onGenerate={handleGenerate} onSaveFavorite={saveFavorite}
          />

          <AnimatePresence>
            {result && (
              <section id="result-anchor" className="mt-12">
                <PlaylistResult key={result.id} generation={result} onRegenerate={() => regenerate(result)} onSaved={s.refreshHistory} />
              </section>
            )}
          </AnimatePresence>

          <section id="history-anchor" className="mt-12">
            <h2 className="font-extrabold text-2xl tracking-tight mb-4">Récemment générées</h2>
            <History items={s.history} onOpen={openGeneration} />
          </section>
        </div>
      </main>

      <Toaster theme="dark" position="bottom-center" toastOptions={TOAST_STYLE} />
    </div>
  );
}
